/**
 * orgUsagePrivacy.tsx — organizational usage visibility (ORG-USAGE-2026).
 *
 * Sibling of guestUsagePrivacy.tsx, and deliberately its MIRROR IMAGE on the
 * one axis that matters:
 *
 *   • The GUEST gate (L4) fails CLOSED. A signed-out viewer must never see
 *     compute detail, so anything short of a confirmed signed-in user is
 *     treated as a guest. That gate runs FIRST and is untouched here.
 *
 *   • This ORG gate fails OPEN. It exists so an organization's super admin can
 *     decide which members see model/token/cost detail — but a signed-in member
 *     must never lose visibility they have today because of a slow read, a
 *     transient RBAC error, or simply because their account predates the
 *     feature. Restriction happens only when we positively know the account is
 *     governed by the new permission model and this member lacks
 *     `usage.view-own`. See hooks/usageVisibility.ts for the full rule.
 *
 * The ⓘ affordance itself ALWAYS remains on every assistant message (pinned by
 * welcomeInfoIcon.test.tsx and the owner directive) — only the popup body
 * changes, exactly like the guest gate.
 *
 * Both popup implementations (TokenInfoPopupContent in GlobalChatSystem.tsx and
 * SpecialTileTokenInfoPopup in SpecialMessageTile.tsx) must consume this in
 * lockstep — they have drifted before.
 */

import React from 'react';
import { getUserUiLanguage } from '../../services/threadMetadata/uiLanguage';
import { useUsageVisibility } from '../../hooks/useUsageVisibility';

// F2 (2026-08-03, owner directive): the ⓘ notice was the only restricted
// surface that stated the fact with no remedy — align it with the Wallet and
// Activity walls so the user always knows the next step.
export const ORG_USAGE_NOTICE_TEXT_EN =
  'Usage details are managed by your organization. Ask your account admin for permission to view usage.';
export const ORG_USAGE_NOTICE_TEXT_ES =
  'Los detalles de uso son administrados por tu organización. Pide a tu administrador de cuenta permiso para ver el uso.';

/**
 * True when this signed-in viewer should NOT see usage detail because their
 * organization restricts it. Fails open — see the header.
 */
export function useUsageDetailRestricted(): boolean {
  const { canSeeUsageDetail } = useUsageVisibility();
  return !canSeeUsageDetail;
}

/**
 * Popup body shown in place of usage/cost details for a signed-in member whose
 * organization has not granted them usage visibility. One uniform message that
 * reveals nothing about compute, mirroring the guest notice's styling.
 */
export const OrgUsageNotice: React.FC = () => {
  const es = getUserUiLanguage() === 'es';
  return (
    <div
      data-testid="org-usage-notice"
      style={{
        fontSize: '0.76rem',
        color: '#94a3b8',
        padding: '10px 12px',
        lineHeight: 1.45,
        background: 'rgba(0, 0, 0, 0.02)',
        borderRadius: '8px',
        border: '1px solid rgba(0, 0, 0, 0.03)',
      }}
    >
      {es ? ORG_USAGE_NOTICE_TEXT_ES : ORG_USAGE_NOTICE_TEXT_EN}
    </div>
  );
};
